import { useEffect, useState } from "react";
import { ArrowUpRight, Lock, Download } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import api, { API } from "../lib/api";

const DEFAULT_LOGO =
  "https://static.prod-images.emergentagent.com/jobs/aec4805b-41b7-45a2-bd9d-09b4932c2e81/images/363110acc2d6e9541b9a04705edae23bb6f65b515136a21d59a1f5597a4e71fc.png";

export default function PublicPage() {
  const [data, setData] = useState({
    site: { name: "CHEAT or not", logo_data_url: null, tagline: null },
    links: [],
    info_blocks: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/site")
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  // Keep tab title and meta description in sync with the site name
  useEffect(() => {
    const name = data.site?.name || "CHEAT or not";
    document.title = name;
    const desc = data.site?.tagline
      ? `${name} — ${data.site.tagline}`
      : `${name} — links e downloads APK em um só lugar.`;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", name);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:title"]', "content", name);
    setMeta('meta[name="twitter:description"]', "content", desc);
    // Update favicon to the uploaded logo if available
    if (data.site?.logo_data_url) {
      let icon = document.querySelector('link[rel="icon"]');
      if (!icon) {
        icon = document.createElement("link");
        icon.rel = "icon";
        document.head.appendChild(icon);
      }
      icon.href = data.site.logo_data_url;
    }
  }, [data.site?.name, data.site?.tagline, data.site?.logo_data_url]);

  const logo = data.site?.logo_data_url || DEFAULT_LOGO;

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-[#050505] text-white"
      data-testid="public-page"
    >
      {/* Background grid + radial */}
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-60" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,255,255,0.12),_transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(57,255,20,0.08),_transparent_55%)]" />

      {/* Top right admin link */}
      <RouterLink
        to="/admin/login"
        className="absolute right-4 top-4 z-20 flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-zinc-400 backdrop-blur-md transition hover:border-cyan-400/60 hover:text-cyan-300"
        data-testid="admin-entry-link"
      >
        <Lock className="h-3 w-3" />
        Admin
      </RouterLink>

      <main className="relative z-10 mx-auto max-w-2xl px-4 py-16 sm:py-24">
        {/* Hero */}
        <header className="reveal flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute -inset-4 rounded-full bg-cyan-400/20 blur-2xl" />
            <img
              src={logo}
              alt="logo"
              className="relative h-28 w-28 rounded-2xl border border-white/10 object-cover shadow-[0_0_40px_rgba(0,255,255,0.25)] sm:h-32 sm:w-32"
              data-testid="site-logo"
            />
          </div>
          <h1
            className="font-display text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl"
            data-testid="site-name"
          >
            <span className="text-white">{data.site?.name?.split(" ")?.slice(0, -1)?.join(" ") || data.site?.name}</span>{" "}
            {data.site?.name?.includes(" ") && (
              <span className="text-cyan-300 text-glow-cyan">
                {data.site?.name?.split(" ").slice(-1)}
              </span>
            )}
          </h1>
          {data.site?.tagline && (
            <p
              className="mt-4 max-w-xl font-mono text-sm text-zinc-400 sm:text-base"
              data-testid="site-tagline"
            >
              {data.site.tagline}
            </p>
          )}
          <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500">
            <span className="h-px w-8 bg-zinc-700" />
            <span>~ Acesso Direto ~</span>
            <span className="h-px w-8 bg-zinc-700" />
          </div>
        </header>

        {/* Links */}
        <section className="mt-12 flex flex-col gap-4" data-testid="links-section">
          {loading && (
            <div className="text-center font-mono text-sm text-zinc-500">
              Carregando...
            </div>
          )}
          {!loading && data.links.length === 0 && data.info_blocks.length === 0 && (
            <div
              className="rounded-xl border border-dashed border-white/10 bg-white/5 p-8 text-center font-mono text-sm text-zinc-500"
              data-testid="empty-state"
            >
              Nenhum link ou informação ainda. Entre no admin para adicionar.
            </div>
          )}
          {data.links.map((link, idx) => {
            const isFile = !!link.file_id;
            const href = isFile ? `${API}/files/${link.file_id}` : link.url;
            return (
              <a
                key={link.id}
                href={href}
                target={isFile ? "_self" : "_blank"}
                rel="noopener noreferrer"
                download={isFile ? link.file_name : undefined}
                className={`group reveal relative flex items-center justify-between rounded-xl border p-5 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 ${
                  isFile
                    ? "border-emerald-400/30 bg-black/60 hover:border-emerald-400 hover:shadow-[0_0_24px_rgba(57,255,20,0.35)]"
                    : "border-white/10 bg-black/60 hover:border-cyan-400 hover:shadow-[0_0_24px_rgba(0,255,255,0.35)]"
                }`}
                style={{ animationDelay: `${idx * 60}ms` }}
                data-testid={`link-card-${link.id}`}
              >
                <div className="flex flex-col text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base font-bold tracking-tight sm:text-lg">
                      {link.title}
                    </span>
                    {isFile && (
                      <span className="rounded border border-emerald-400/40 bg-emerald-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300">
                        APK
                      </span>
                    )}
                  </div>
                  {link.description && (
                    <span className="mt-1 font-mono text-xs text-zinc-400 sm:text-sm">
                      {link.description}
                    </span>
                  )}
                  {isFile && link.file_size && (
                    <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {formatBytes(link.file_size)}
                    </span>
                  )}
                </div>
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-black/40 transition ${
                    isFile
                      ? "border-emerald-400/30 text-emerald-300 group-hover:border-emerald-400 group-hover:bg-emerald-400/10"
                      : "border-white/10 text-zinc-400 group-hover:border-cyan-400 group-hover:bg-cyan-400/10 group-hover:text-cyan-300"
                  }`}
                >
                  {isFile ? (
                    <Download className="h-4 w-4" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4" />
                  )}
                </span>
              </a>
            );
          })}
        </section>

        {/* Info Blocks */}
        {data.info_blocks.length > 0 && (
          <section
            className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2"
            data-testid="info-section"
          >
            {data.info_blocks.map((block, idx) => (
              <article
                key={block.id}
                className="reveal rounded-xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md transition hover:border-emerald-400/40"
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={`info-block-${block.id}`}
              >
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                  {block.title}
                </h3>
                <p className="mt-3 whitespace-pre-wrap font-mono text-sm leading-relaxed text-zinc-300">
                  {block.content}
                </p>
              </article>
            ))}
          </section>
        )}

        {/* Footer */}
        <footer className="mt-20 border-t border-white/5 pt-6 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          // {data.site?.name || "CHEAT or not"} — system online
        </footer>
      </main>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
